import React, { Suspense, useEffect } from 'react';
import {
  Title,
  TitleSizes,
  Skeleton,
  Button,
  Alert,
  Icon,
} from '@patternfly/react-core';
import { extractFromSlug } from 'helpers/utils';
import { KalturaWidget } from '@ole-ui/ole-ui-components';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import PlayIcon from '@patternfly/react-icons/dist/esm/icons/play-icon';
import GenericViewErrorV2 from 'components/Error/GenericViewErrorV2';
import { handleError } from 'services/ErrorService';
import ViewLoader from 'components/Loading/View';
import Separator from 'components/Separator';
import useKalturaSession from 'hooks/useKalturaSession';
import VersionsDropdown from 'components/Versions/Dropdown';
import useMobxStores from 'hooks/useMobxStores';
import TechOverviewStore from './store';

import './styles.scss';

const TechOverview = () => {
  const { t } = useTranslation();
  const { routerStore, catalogStore, configStore, progressStore } =
    useMobxStores();

  const { slug } = routerStore?.router?.getState().params;
  const { code, version } = extractFromSlug(slug);

  const store = useLocalStore(
    () => new TechOverviewStore(catalogStore, slug, progressStore),
  );
  const {
    isLoading,
    title,
    videoEntryId,
    playerPartnerId,
    playerUIConfigId,
    isPlaylist,
    lastWatchedVideo,
    lastWatchedTimestamp,
    isReadyForPlayer,
  } = store;

  let startTime: number;
  let entryId: string;
  let startAtEntryId: string;

  if (isReadyForPlayer) {
    entryId = isPlaylist ? videoEntryId : lastWatchedVideo || videoEntryId;
    startAtEntryId = isPlaylist ? lastWatchedVideo : '';
    startTime = Math.floor(lastWatchedTimestamp || 0);
  }

  const { kalturaSession, skipKalturaSession } = useKalturaSession({
    entryId,
  });

  const onVideoPlayerLaunch = (videoPlayer) => {
    videoPlayer.addEventListener(
      videoPlayer.Event.Core.TIME_UPDATE,
      (event) => {
        store.updateProgress(event.target);
      },
    );
    videoPlayer.addEventListener(videoPlayer.Event.Core.PLAY, () => {
      store.startPolling();
    });
    videoPlayer.addEventListener(videoPlayer.Event.Core.PAUSE, () => {
      store.stopPolling();
    });
    videoPlayer.addEventListener(videoPlayer.Event.Core.PLAYER_DESTROY, () => {
      store.stopPolling();
    });
  };

  const isKalturaSessionResolved = kalturaSession || skipKalturaSession;

  const handleSelectVersion = (selectedVersion: string) => {
    routerStore.navigate('tech-overview:view', {
      slug: `${code}-${selectedVersion}`,
    });
  };

  useEffect(() => {
    store.getTechOverview(slug);
  }, [slug]);

  useEffect(() => {
    store.setVersions();
  }, [catalogStore.entries]);

  return (
    <Suspense fallback={<ViewLoader />}>
      <div className="container-grid-responsive tech-overview">
        {isLoading ? (
          <Skeleton
            width="75%"
            fontSize="4xl"
            screenreaderText={t('Loading content')}
          />
        ) : (
          <Title
            className="tech-overview__title"
            headingLevel="h1"
            size={TitleSizes['4xl']}
          >
            {title || t('Technical overview')}
          </Title>
        )}
        <Separator />
        <div className="tech-overview__actions">
          {store.versions?.length > 1 && (
            <VersionsDropdown
              className="tech-overview__versions-dropdown"
              code={code}
              currentVersion={version}
              isLatestVersion={store.isLatestVersion}
              onOptionSelect={handleSelectVersion}
              versions={store.versions}
              tooltipPosition="right"
            />
          )}

          <Button
            variant="primary"
            component="a"
            target="_blank"
            href={`${configStore.config.SURVEY_URL}?modality=tov&course=${code}`}
            isDisabled={isLoading}
          >
            {t('Feedback')}
          </Button>
        </div>

        {(store.versions?.length > 0 || !store.payload) &&
          isReadyForPlayer &&
          isKalturaSessionResolved && (
            <KalturaWidget
              className="tech-overview__player"
              partnerId={playerPartnerId}
              playerId={playerUIConfigId}
              entryId={entryId}
              session={kalturaSession}
              isPlaylist={isPlaylist}
              onPlayerLaunched={onVideoPlayerLaunch}
              startAtEntryId={startAtEntryId}
              startTime={startTime}
            />
          )}

        {(store.versions?.length > 0 || !store.payload) &&
          !(isReadyForPlayer && isKalturaSessionResolved) && (
            <div className="tech-overview__skeleton-container">
              <Icon className="tech-overview__skeleton-icon" size="lg">
                <PlayIcon />
              </Icon>
              <Skeleton height="100%" screenreaderText={t('Loading content')} />
            </div>
          )}

        {!(store.versions?.length > 0 || !store.payload) && !isLoading && (
          <Alert variant="danger" title={t('This content is not available.')} />
        )}
      </div>
    </Suspense>
  );
};

export default withErrorBoundary(
  observer(TechOverview),
  GenericViewErrorV2,
  handleError,
);
